import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import PageHelpButton from '../components/help/PageHelpButton';
import {
  Upload, FileSpreadsheet, Users, Briefcase, Building2, Receipt, FileText, Boxes,
  ChevronRight, Check, AlertCircle, Loader2, Download, Sparkles, ArrowLeft, ScanLine,
  UserPlus, UserCheck, ShieldAlert, ShieldCheck,
} from 'lucide-react';

const ENTITIES = [
  { key: 'clients', label: 'Clientes', icon: Users, color: 'bg-blue-100 text-blue-700' },
  { key: 'services', label: 'Servicios', icon: Briefcase, color: 'bg-emerald-100 text-emerald-700' },
  { key: 'articles', label: 'Artículos', icon: Boxes, color: 'bg-violet-100 text-violet-700' },
  { key: 'providers', label: 'Proveedores', icon: Building2, color: 'bg-amber-100 text-amber-700' },
  { key: 'expenses', label: 'Gastos', icon: Receipt, color: 'bg-rose-100 text-rose-700' },
  { key: 'invoices', label: 'Facturas emitidas', icon: FileText, color: 'bg-purple-100 text-purple-700' },
];

const ImportPage = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1=choose entity, 2=upload, 3=mapping, 4=result
  const [entity, setEntity] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);
  const [analysis, setAnalysis] = useState(null); // invoice analyze report
  const [analyzing, setAnalyzing] = useState(false);
  const [pdfMode, setPdfMode] = useState(false);  // PDF (IA) upload for invoices
  const [pdfFiles, setPdfFiles] = useState([]);   // multi-file selection for PDF
  const [pdfProgress, setPdfProgress] = useState(null);  // { current, total, file }
  const [clientMatch, setClientMatch] = useState(null);  // {summary, preview_new, preview_existing}
  const [matchingClients, setMatchingClients] = useState(false);
  const [nifReport, setNifReport] = useState(null);  // {invalid:[], duplicate_groups:[], valid_count, …}
  const [validatingNifs, setValidatingNifs] = useState(false);
  const inputRef = useRef(null);
  const pdfInputRef = useRef(null);

  const BG_THRESHOLD = 100;  // use background job if total_rows >= this

  // Pre-select entity if ?entity=clients (etc.) is in the URL
  useEffect(() => {
    const pre = searchParams.get('entity');
    if (pre && step === 1) {
      const found = ENTITIES.find((e) => e.key === pre);
      if (found) {
        setEntity(found);
        setStep(2);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Run NIF/CIF validator against AEAT spec whenever mapping or preview changes
  useEffect(() => {
    if (step !== 3 || !preview?.preview || !entity?.key) { setNifReport(null); return; }
    const nifKey = entity.key === 'invoices' ? 'client_tax_id' : 'tax_id';
    if (!mapping?.[nifKey]) { setNifReport(null); return; }
    let cancel = false;
    (async () => {
      setValidatingNifs(true);
      try {
        const { data } = await api.post(`/imports/${entity.key}/validate-nifs`, {
          rows: preview.preview, mapping,
        });
        if (!cancel) setNifReport(data);
      } catch { if (!cancel) setNifReport(null); }
      finally { if (!cancel) setValidatingNifs(false); }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, entity?.key, mapping?.tax_id, mapping?.client_tax_id, preview]);

  const pickEntity = (e) => { setEntity(e); setStep(2); setPreview(null); setFile(null); setResult(null); setAnalysis(null); setPdfMode(false); setPdfFiles([]); setClientMatch(null); setNifReport(null); };
  const backToEntities = () => { setStep(1); setEntity(null); setPreview(null); setFile(null); setResult(null); setAnalysis(null); setPdfMode(false); setPdfFiles([]); setClientMatch(null); setNifReport(null); };

  // For invoices: auto-fetch which clients exist and which will be created
  const runClientMatch = async (rows, currentMapping) => {
    if (!rows?.length || !currentMapping?.client_name) return;
    setMatchingClients(true);
    try {
      const { data } = await api.post('/imports/invoices/match-clients', {
        rows, mapping: currentMapping,
      });
      setClientMatch(data);
    } catch (_e) {
      setClientMatch(null);
    } finally {
      setMatchingClients(false);
    }
  };

  // Re-run match whenever mapping changes (debounced via stable references)
  useEffect(() => {
    if (step === 3 && entity?.key === 'invoices' && preview?.preview && mapping?.client_name) {
      runClientMatch(preview.preview, mapping);
    } else {
      setClientMatch(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, mapping?.client_name, mapping?.client_tax_id, entity?.key, preview]);

  const downloadTemplate = async () => {
    if (!entity) return;
    try {
      const resp = await api.get(`/imports/${entity.key}/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = `plantilla_${entity.key}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Error descargando plantilla'); }
  };

  const onFile = async (f) => {
    if (!f) return;
    setFile(f); setBusy(true); setPreview(null);
    const fd = new FormData(); fd.append('file', f);
    try {
      const { data } = await api.post(`/imports/${entity.key}/preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(data); setMapping(data.suggested_mapping || {});
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error leyendo archivo');
    } finally { setBusy(false); }
  };

  const onPdfFiles = async (filesList) => {
    const arr = Array.from(filesList || []).filter((f) => /\.(pdf|jpg|jpeg|png|webp)$/i.test(f.name));
    if (!arr.length) { toast.error('Sube uno o varios PDFs o imágenes de facturas'); return; }
    if (arr.length > 20) { toast.error('Máximo 20 archivos por importación'); return; }
    setPdfFiles(arr); setBusy(true); setPreview(null); setPdfProgress(null);
    const fd = new FormData();
    arr.forEach((f) => fd.append('files', f));
    try {
      // 1) Start background job — returns job_id immediately, avoiding proxy timeouts
      const { data: startResp } = await api.post('/imports/invoices/pdf-preview/start', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const { job_id } = startResp;

      // 2) Poll status every 3s — up to 5 minutes
      const maxAttempts = 100;
      let attempt = 0;
      let finalData = null;
      while (attempt < maxAttempts) {
        await new Promise((r) => setTimeout(r, 3000));
        attempt += 1;
        let statusResp;
        try {
          statusResp = await api.get(`/imports/invoices/pdf-preview/status/${job_id}`, { timeout: 15000 });
        } catch (_pollErr) {
          continue;  // transient poll error → keep trying
        }
        const j = statusResp.data;
        setPdfProgress({ current: j.progress, total: j.total, file: j.current_file });
        if (j.status === 'done') { finalData = j.result; break; }
        if (j.status === 'error') {
          if (j.error_code === 'BUDGET_EXHAUSTED') {
            toast.error(j.error, { duration: 10000 });
          } else {
            toast.error(j.error || 'Error procesando PDFs');
          }
          return;
        }
      }

      if (!finalData) { toast.error('El procesamiento tardó demasiado. Intenta con menos PDFs.'); return; }

      setPreview(finalData);
      setMapping(finalData.suggested_mapping || {});
      setStep(3);
      const filesMsg = finalData.files_processed > 1 ? ` de ${finalData.files_processed} archivo(s)` : '';
      if (finalData.budget_exhausted) {
        toast.warning(`${finalData.total_rows} factura(s) extraídas${filesMsg} antes de agotarse el saldo. Recarga tu Universal Key.`, { duration: 8000 });
      } else if (finalData.errors?.length) {
        toast.warning(`${finalData.total_rows} factura(s) extraídas${filesMsg} · ${finalData.errors.length} con error`);
      } else {
        toast.success(`${finalData.total_rows} factura(s) extraídas${filesMsg}`);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402) {
        toast.error(detail || 'Saldo de IA agotado. Recarga tu Universal Key.', { duration: 10000 });
      } else if (status === 413) {
        toast.error('Archivo demasiado grande. Reduce el tamaño o el número de páginas.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Error procesando PDFs');
      }
    } finally { setBusy(false); setPdfProgress(null); }
  };

  // Analysis flow for invoices (pre-commit validation report)
  const runAnalysis = async () => {
    if (!file && !pdfMode) return;
    setAnalyzing(true);
    setAnalysis(null);
    try {
      let rowsToAnalyze;
      if (pdfMode) {
        // PDF mode: rows are already in preview.preview (no re-fetch needed)
        rowsToAnalyze = preview?.preview || [];
      } else {
        const fd = new FormData();
        fd.append('file', file);
        const { data: allData } = await api.post(`/imports/${entity.key}/preview?all=true`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        rowsToAnalyze = allData.preview;
      }
      const { data } = await api.post('/imports/invoices/analyze', { rows: rowsToAnalyze, mapping });
      setAnalysis(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al analizar el archivo');
    } finally {
      setAnalyzing(false);
    }
  };

  const commitInvoicesAdvanced = async (mode, archiveSeries) => {
    if (!file && !pdfMode) return;
    setBusy(true);
    try {
      let rowsToCommit;
      if (pdfMode) {
        rowsToCommit = preview?.preview || [];
      } else {
        const fd = new FormData();
        fd.append('file', file);
        const { data: allData } = await api.post(`/imports/${entity.key}/preview?all=true`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        rowsToCommit = allData.preview;
      }
      const { data } = await api.post('/imports/invoices/commit-advanced', {
        rows: rowsToCommit,
        mapping,
        mode,
        archive_target_series: archiveSeries || null,
      });
      setResult(data);
      setStep(4);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((detail && detail.message) || detail || 'Error al importar facturas');
    } finally {
      setBusy(false);
    }
  };

  // Re-parse the full file and commit ALL rows. Uses background job if large.
  const commitFull = async () => {
    if (!file) return;
    setBusy(true);
    setJobProgress(null);
    try {
      const useBackground = (preview?.total_rows || 0) >= BG_THRESHOLD;
      if (useBackground) {
        // Submit job
        const fd = new FormData();
        fd.append('file', file);
        fd.append('mapping_json', JSON.stringify(mapping));
        fd.append('skip_duplicates', skipDuplicates ? 'true' : 'false');
        const { data: jobInit } = await api.post(`/imports/${entity.key}/job`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setJobProgress({ ...jobInit, progress_pct: 0, processed: 0 });
        setStep(4);
        // Poll
        const poll = async () => {
          const { data: j } = await api.get(`/imports/jobs/${jobInit.job_id}`);
          setJobProgress(j);
          if (j.status === 'completed' || j.status === 'failed') {
            setResult({
              imported: j.imported,
              skipped: j.skipped,
              errors: j.errors,
              total: j.total,
            });
            setBusy(false);
            return;
          }
          setTimeout(poll, 1000);
        };
        poll();
      } else {
        // Synchronous commit (small files)
        const fd = new FormData();
        fd.append('file', file);
        const { data: allData } = await api.post(`/imports/${entity.key}/preview?all=true`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const { data } = await api.post(`/imports/${entity.key}/commit`, {
          rows: allData.preview,
          mapping,
          skip_duplicates: skipDuplicates,
        });
        setResult(data);
        setStep(4);
        setBusy(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al importar');
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="import-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-stone-900 flex items-center gap-2">
              <Upload className="w-8 h-8 text-[#FF6B4A]" /> Importar datos
            </h1>
            <p className="text-sm text-stone-500 mt-1">Sube Excel/CSV — o ahora también <strong className="text-violet-700">facturas en PDF</strong> con extracción por IA.</p>
          </div>
          <PageHelpButton moduleKey="import" variant="icon" />
        </div>
        {step > 1 && (
          <Button variant="ghost" onClick={backToEntities} data-testid="back-to-entities">
            <ArrowLeft className="w-4 h-4 mr-1" /> Cambiar entidad
          </Button>
        )}
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 text-sm text-stone-500">
        {['Entidad', 'Archivo', 'Mapeo', 'Resultado'].map((label, i) => (
          <React.Fragment key={i}>
            <span className={`flex items-center gap-1.5 ${step > i ? 'text-[#FF6B4A] font-medium' : step === i + 1 ? 'text-stone-900 font-bold' : ''}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${step > i + 1 ? 'bg-[#FF6B4A] text-white' : step === i + 1 ? 'bg-stone-900 text-white' : 'bg-stone-200'}`}>
                {step > i + 1 ? <Check className="w-3 h-3" /> : i + 1}
              </span>
              {label}
            </span>
            {i < 3 && <ChevronRight className="w-3 h-3 opacity-40" />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1: Choose entity */}
      {step === 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="entity-grid">
          {ENTITIES.map((e) => {
            const Icon = e.icon;
            return (
              <button
                key={e.key}
                onClick={() => pickEntity(e)}
                className="text-left bg-white rounded-2xl border border-stone-100 p-5 hover:shadow-md hover:border-stone-200 transition"
                data-testid={`pick-entity-${e.key}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${e.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-stone-900">{e.label}</h3>
                <p className="text-xs text-stone-500 mt-1">
                  {e.key === 'invoices' ? (
                    <>Excel/CSV o <strong className="text-violet-700">PDF con IA</strong></>
                  ) : 'Importar desde Excel o CSV'}
                </p>
              </button>
            );
          })}
          <Link
            to="/expenses/inbox"
            className="text-left bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-2xl border border-violet-100 p-5 hover:shadow-md transition group"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-violet-200 text-violet-700">
              <ScanLine className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-violet-900 flex items-center gap-1">PDFs / Tickets <Sparkles className="w-4 h-4" /></h3>
            <p className="text-xs text-violet-700 mt-1">Sube PDFs e imágenes — la IA extrae proveedor, base e IVA automáticamente.</p>
          </Link>
        </div>
      )}

      {/* STEP 2: Upload */}
      {step === 2 && entity && (
        <div className="bg-white rounded-2xl border border-stone-100 p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-lg font-bold text-stone-900">Subir archivo: {entity.label}</h2>
            <div className="flex items-center gap-2">
              {entity.key === 'invoices' && (
                <div className="inline-flex bg-stone-100 rounded-full p-1" data-testid="import-mode-toggle">
                  <button
                    onClick={() => { setPdfMode(false); setPdfFiles([]); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${!pdfMode ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}
                    data-testid="import-mode-excel"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 inline mr-1" /> Excel / CSV
                  </button>
                  <button
                    onClick={() => { setPdfMode(true); setFile(null); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${pdfMode ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                    data-testid="import-mode-pdf"
                  >
                    <Sparkles className="w-3.5 h-3.5 inline mr-1" /> PDFs con IA
                  </button>
                </div>
              )}
              {!pdfMode && (
                <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
                  <Download className="w-4 h-4 mr-1" /> Plantilla Excel
                </Button>
              )}
            </div>
          </div>

          {!pdfMode ? (
            <div
              className="border-2 border-dashed border-stone-200 rounded-2xl p-10 text-center hover:border-[#FF6B4A] hover:bg-orange-50/30 transition cursor-pointer"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) onFile(e.dataTransfer.files[0]); }}
              data-testid="upload-zone"
            >
              <Upload className="w-12 h-12 mx-auto text-stone-300 mb-3" />
              <p className="text-sm font-medium text-stone-700">Arrastra aquí o haz clic para seleccionar</p>
              <p className="text-xs text-stone-500 mt-1">Formatos: .xlsx, .xls, .csv · Máx 5 MB</p>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls,.csv,.txt"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
                data-testid="file-input"
              />
            </div>
          ) : (
            <div
              className="border-2 border-dashed border-violet-200 rounded-2xl p-10 text-center hover:border-violet-400 hover:bg-violet-50/30 transition cursor-pointer relative overflow-hidden"
              onClick={() => pdfInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); onPdfFiles(e.dataTransfer.files); }}
              data-testid="upload-zone-pdf"
            >
              <div className="absolute top-3 right-3 text-[10px] font-extrabold tracking-widest uppercase bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white px-2 py-1 rounded-full">
                IA · Claude Sonnet 4.5
              </div>
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <p className="text-base font-bold text-stone-800">Arrastra PDFs o imágenes de facturas</p>
              <p className="text-xs text-stone-500 mt-1">Hasta 20 archivos · <strong className="text-violet-700">un PDF puede contener varias facturas</strong> y se extraen todas · PDF/JPG/PNG · Máx 12MB cada uno</p>
              <input
                ref={pdfInputRef}
                type="file"
                accept=".pdf,image/*"
                multiple
                className="hidden"
                onChange={(e) => onPdfFiles(e.target.files)}
                data-testid="pdf-file-input"
              />
            </div>
          )}

          {busy && (
            <div className="mt-4 bg-violet-50 border border-violet-100 rounded-xl p-4" data-testid="upload-busy">
              <p className="text-sm font-bold text-violet-900 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {pdfMode ? (
                  pdfProgress
                    ? `Procesando ${pdfProgress.file || ''} (${pdfProgress.current}/${pdfProgress.total} archivos)…`
                    : `Enviando ${pdfFiles.length} archivo(s) a la IA…`
                ) : 'Analizando archivo…'}
              </p>
              {pdfMode && (
                <p className="text-xs text-violet-700 mt-1">
                  La IA procesa cada PDF en paralelo. Un documento de 10-15 páginas tarda ~30s.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* STEP 3: Mapping + Preview */}
      {step === 3 && preview && (
        <div className="space-y-4">
          {preview.source === 'pdf' && (
            <div className="bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-100 rounded-2xl p-4" data-testid="pdf-preview-banner">
              <p className="text-sm font-bold text-violet-900 flex items-center gap-1.5">
                <Sparkles className="w-4 h-4" />
                {preview.total_rows} factura{preview.total_rows === 1 ? '' : 's'} extraída{preview.total_rows === 1 ? '' : 's'} por la IA
                {preview.files_processed > 1 && ` de ${preview.files_processed} archivo${preview.files_processed === 1 ? '' : 's'}`}
              </p>
              <p className="text-xs text-violet-700 mt-1">Revisa los datos abajo. Puedes mapear/ajustar antes de importar.</p>
              {preview.budget_exhausted && (
                <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2 mt-2 font-bold">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  Saldo de IA agotado durante el procesamiento. Recarga tu Universal Key (Profile → Universal Key → Add Balance) para procesar el resto.
                </p>
              )}
              {preview.errors?.length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-rose-700 font-bold">{preview.errors.length} archivo(s) con error</summary>
                  <ul className="mt-1 pl-3 list-disc space-y-0.5">
                    {preview.errors.map((er, i) => <li key={i}><span className="font-mono">{er.file}</span>: {er.error}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* AEAT NIF/CIF validator warning */}
          {(validatingNifs || (nifReport && (nifReport.invalid_count > 0 || nifReport.duplicate_groups?.length > 0))) && (
            <div
              className={`rounded-2xl border p-5 ${nifReport?.invalid_count > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-stone-100'}`}
              data-testid="nif-validator-card"
            >
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-base font-extrabold font-heading tracking-tight flex items-center gap-2 text-stone-900">
                  {nifReport?.invalid_count > 0 ? (
                    <ShieldAlert className="w-4 h-4 text-amber-600" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  )}
                  Validación NIF/CIF/NIE (AEAT)
                  {validatingNifs && <Loader2 className="w-3.5 h-3.5 animate-spin text-stone-400" />}
                </h3>
                {nifReport && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full font-bold border border-emerald-100">
                      <ShieldCheck className="w-3 h-3" /> {nifReport.valid_count} válidos
                    </span>
                    {nifReport.invalid_count > 0 && (
                      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-1 rounded-full font-bold border border-amber-200">
                        <ShieldAlert className="w-3 h-3" /> {nifReport.invalid_count} sospechoso{nifReport.invalid_count === 1 ? '' : 's'}
                      </span>
                    )}
                    {nifReport.empty_count > 0 && (
                      <span className="inline-flex items-center gap-1 bg-stone-50 text-stone-600 px-2 py-1 rounded-full font-bold border border-stone-200">
                        {nifReport.empty_count} sin NIF
                      </span>
                    )}
                    {nifReport.duplicate_groups?.length > 0 && (
                      <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 px-2 py-1 rounded-full font-bold border border-rose-100">
                        <AlertCircle className="w-3 h-3" /> {nifReport.duplicate_groups.length} duplicado{nifReport.duplicate_groups.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {nifReport?.invalid_count > 0 && (
                <div data-testid="nif-invalid-list">
                  <p className="text-xs text-amber-800 mb-2">
                    Los siguientes NIF/CIF no cumplen la letra/dígito de control de la AEAT.
                    Puedes seguir importando — quedarán marcados como sospechosos en la ficha.
                  </p>
                  <div className="bg-white/60 rounded-xl border border-amber-200 overflow-hidden">
                    <table className="text-xs w-full">
                      <thead className="bg-amber-100/60 text-amber-900">
                        <tr>
                          <th className="px-3 py-2 text-left">Fila</th>
                          <th className="px-3 py-2 text-left">Nombre</th>
                          <th className="px-3 py-2 text-left">NIF/CIF</th>
                          <th className="px-3 py-2 text-left">Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nifReport.invalid.slice(0, 12).map((row, i) => (
                          <tr key={i} className="border-t border-amber-100">
                            <td className="px-3 py-1.5 font-mono text-stone-600">{row.row}</td>
                            <td className="px-3 py-1.5 font-medium text-stone-900 truncate max-w-[14rem]">{row.name || <em className="text-stone-400">—</em>}</td>
                            <td className="px-3 py-1.5 font-mono text-amber-900">{row.tax_id}</td>
                            <td className="px-3 py-1.5 text-amber-800">{row.reason}</td>
                          </tr>
                        ))}
                        {nifReport.invalid.length > 12 && (
                          <tr><td colSpan={4} className="px-3 py-2 text-[10px] text-stone-500 italic">… y {nifReport.invalid.length - 12} más</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {nifReport?.duplicate_groups?.length > 0 && (
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer font-bold text-rose-700">NIFs duplicados en el archivo</summary>
                  <ul className="mt-2 pl-4 list-disc text-rose-700 space-y-0.5">
                    {nifReport.duplicate_groups.slice(0, 20).map((d, i) => (
                      <li key={i}><span className="font-mono">{d.tax_id}</span> en filas {d.rows.join(', ')}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {entity?.key === 'invoices' && (matchingClients || clientMatch) && (
            <div className="bg-white rounded-2xl border border-stone-100 p-5" data-testid="client-match-card">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-base font-extrabold font-heading tracking-tight flex items-center gap-2 text-stone-900">
                  <Users className="w-4 h-4 text-violet-500" />
                  Clientes detectados
                  {matchingClients && <Loader2 className="w-3.5 h-3.5 animate-spin text-stone-400" />}
                </h3>
                {clientMatch && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full font-bold border border-emerald-100">
                      <UserCheck className="w-3 h-3" /> {clientMatch.summary.matched_existing} existente{clientMatch.summary.matched_existing === 1 ? '' : 's'}
                    </span>
                    <span className="inline-flex items-center gap-1 bg-violet-50 text-violet-700 px-2 py-1 rounded-full font-bold border border-violet-100">
                      <UserPlus className="w-3 h-3" /> {clientMatch.summary.to_create} se crearán
                    </span>
                    {clientMatch.summary.rows_without_client > 0 && (
                      <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 px-2 py-1 rounded-full font-bold border border-rose-100">
                        <AlertCircle className="w-3 h-3" /> {clientMatch.summary.rows_without_client} sin cliente
                      </span>
                    )}
                  </div>
                )}
              </div>
              {clientMatch && clientMatch.preview_new?.length === 0 && clientMatch.preview_existing?.length === 0 ? (
                <p className="text-xs text-stone-500">Asegúrate de mapear el campo <strong>Cliente (nombre)</strong> para ver el matching.</p>
              ) : clientMatch && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {clientMatch.preview_new?.length > 0 && (
                    <div className="bg-violet-50/50 border border-violet-100 rounded-xl p-3">
                      <p className="text-[10px] uppercase font-extrabold tracking-widest text-violet-700 mb-2 flex items-center gap-1">
                        <UserPlus className="w-3 h-3" /> Se crearán automáticamente ({clientMatch.preview_new.length})
                      </p>
                      <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="clients-to-create">
                        {clientMatch.preview_new.slice(0, 20).map((c, i) => (
                          <li key={i} className="text-xs flex items-center justify-between gap-2 bg-white rounded px-2 py-1">
                            <span className="font-bold text-stone-800 truncate">{c.name || <em className="text-stone-400">Sin nombre</em>}</span>
                            <span className="font-mono text-[10px] text-stone-500 flex-shrink-0">{c.tax_id || '—'}</span>
                          </li>
                        ))}
                        {clientMatch.preview_new.length > 20 && (
                          <li className="text-[10px] text-stone-500 italic px-2">…y {clientMatch.preview_new.length - 20} más</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {clientMatch.preview_existing?.length > 0 && (
                    <div className="bg-emerald-50/40 border border-emerald-100 rounded-xl p-3">
                      <p className="text-[10px] uppercase font-extrabold tracking-widest text-emerald-700 mb-2 flex items-center gap-1">
                        <UserCheck className="w-3 h-3" /> Ya existentes en tu base ({clientMatch.preview_existing.length})
                      </p>
                      <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="clients-matched">
                        {clientMatch.preview_existing.slice(0, 20).map((c, i) => (
                          <li key={i} className="text-xs flex items-center justify-between gap-2 bg-white rounded px-2 py-1">
                            <span className="font-bold text-stone-800 truncate">{c.name}</span>
                            <span className="font-mono text-[10px] text-stone-500 flex-shrink-0">{c.tax_id || '—'}</span>
                          </li>
                        ))}
                        {clientMatch.preview_existing.length > 20 && (
                          <li className="text-[10px] text-stone-500 italic px-2">…y {clientMatch.preview_existing.length - 20} más</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-2xl border border-stone-100 p-5">
            <h2 className="text-lg font-bold text-stone-900 mb-2">Mapeo de columnas</h2>
            <p className="text-xs text-stone-500 mb-4">Hemos detectado {preview.total_rows} filas con {preview.headers.length} columnas. Verifica que cada campo corresponde con su columna.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {preview.fields.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <label className="w-44 text-sm text-stone-700 flex-shrink-0">
                    {f.label}{f.required && <span className="text-rose-500">*</span>}
                  </label>
                  <Select value={mapping[f.key] || '__NONE__'} onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v === '__NONE__' ? '' : v }))}>
                    <SelectTrigger className="flex-1 rounded-xl" data-testid={`mapping-${f.key}`}>
                      <SelectValue placeholder="(sin mapear)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__NONE__">(sin mapear)</SelectItem>
                      {preview.headers.map((h) => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            {(() => {
              const mappedCols = new Set(Object.values(mapping).filter(Boolean));
              const unmapped = preview.headers.filter((h) => !mappedCols.has(h));
              if (unmapped.length === 0 || !['clients', 'providers'].includes(entity?.key)) return null;
              return (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-xl">
                  <p className="text-xs font-bold text-blue-900 mb-1 inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Columnas extra que se guardarán como <strong>campos personalizados</strong>:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {unmapped.map((u) => (
                      <span key={u} className="text-[10px] font-mono px-1.5 py-0.5 bg-white border border-blue-200 rounded">{u}</span>
                    ))}
                  </div>
                  <p className="text-[10px] text-blue-700 mt-2">Estas columnas no se perderán: se guardan en el cliente/proveedor y podrás verlas en su ficha.</p>
                </div>
              );
            })()}
            <label className="flex items-center gap-2 mt-4 text-sm text-stone-700">
              <input type="checkbox" checked={skipDuplicates} onChange={(e) => setSkipDuplicates(e.target.checked)} data-testid="skip-duplicates" />
              Saltar duplicados (NIF/número factura ya existente)
            </label>
            {(preview?.total_rows || 0) >= BG_THRESHOLD && (
              <p className="mt-3 text-xs text-amber-700 bg-amber-50 rounded-lg p-2 border border-amber-100">
                <AlertCircle className="w-3 h-3 inline mr-1" /> Archivo grande ({preview.total_rows} filas): se importará en <strong>segundo plano</strong> con barra de progreso. Puedes cerrar la página y volver.
              </p>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-stone-100 p-5 overflow-x-auto">
            <h3 className="font-bold text-stone-900 text-sm mb-3">Vista previa (primeras 10 filas)</h3>
            <table className="text-xs w-full">
              <thead className="bg-stone-50">
                <tr>{preview.headers.map((h) => <th key={h} className="px-2 py-1 text-left whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {preview.preview.slice(0, 10).map((row, i) => (
                  <tr key={i} className="border-t border-stone-100">
                    {preview.headers.map((h) => <td key={h} className="px-2 py-1 whitespace-nowrap">{String(row[h] ?? '')}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Invoice-specific: pre-import analysis report */}
          {entity?.key === 'invoices' && analysis && (
            <div className="bg-white rounded-2xl border border-stone-100 p-5 space-y-4" data-testid="analysis-report">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-bold text-stone-900 text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#FF6B4A]" /> Informe previo a la importación
                </h3>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${analysis.can_import ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {analysis.can_import ? 'LISTO PARA IMPORTAR' : 'CORREGIR ANTES DE IMPORTAR'}
                </span>
              </div>

              {/* Groups summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {analysis.groups.map((g) => (
                  <div key={`${g.series}-${g.year}`} className="bg-stone-50 rounded-xl px-3 py-2 border border-stone-100">
                    <div className="font-bold text-stone-900">{g.series}/{g.year}</div>
                    <div className="text-stone-500">{g.count} factura{g.count === 1 ? '' : 's'} · #{g.min_number}–{g.max_number}</div>
                  </div>
                ))}
              </div>

              {/* Errors blockers */}
              {analysis.duplicates.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="duplicates-block">
                  <p className="font-bold text-rose-800 text-sm mb-1"><AlertCircle className="w-4 h-4 inline" /> {analysis.duplicates.length} número(s) duplicado(s) — IMPIDE IMPORTAR</p>
                  <ul className="text-xs text-rose-700 space-y-0.5 max-h-32 overflow-y-auto pl-5 list-disc">
                    {analysis.duplicates.slice(0, 20).map((d, i) => (
                      <li key={i}>Serie <strong>{d.series}/{d.year}</strong>, número <strong>{d.number}</strong> en filas {d.rows.join(', ')}</li>
                    ))}
                  </ul>
                </div>
              )}
              {analysis.unparseable.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
                  <p className="font-bold text-rose-800 text-sm mb-1">⚠ {analysis.unparseable.length} fila(s) con número irreconocible</p>
                  <ul className="text-xs text-rose-700 space-y-0.5 max-h-24 overflow-y-auto pl-5 list-disc">
                    {analysis.unparseable.slice(0, 10).map((u, i) => (<li key={i}>Fila {u.row}: "{u.invoice_number || '(vacío)'}"</li>))}
                  </ul>
                </div>
              )}

              {/* Warnings */}
              {analysis.gaps.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="gaps-warning">
                  <p className="font-bold text-amber-800 text-sm mb-1">⚠ Huecos en la numeración (decide si es correcto)</p>
                  <ul className="text-xs text-amber-700 space-y-0.5 max-h-24 overflow-y-auto pl-5 list-disc">
                    {analysis.gaps.map((g, i) => (<li key={i}>Serie <strong>{g.series}/{g.year}</strong>: faltan {g.missing_count} ({g.missing.slice(0, 10).join(', ')}{g.missing.length > 10 ? '…' : ''})</li>))}
                  </ul>
                </div>
              )}
              {analysis.chronology_issues.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="chronology-warning">
                  <p className="font-bold text-amber-800 text-sm mb-1">⚠ Fechas incoherentes con la numeración</p>
                  <ul className="text-xs text-amber-700 space-y-0.5 max-h-24 overflow-y-auto pl-5 list-disc">
                    {analysis.chronology_issues.slice(0, 10).map((c, i) => (
                      <li key={i}>Fila {c.row} ({c.series}/{c.year} nº{c.number}): {c.date} es anterior a la nº{c.previous_number} ({c.previous_date})</li>
                    ))}
                  </ul>
                </div>
              )}
              {analysis.invalid_nif.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="nif-warning">
                  <p className="font-bold text-amber-800 text-sm mb-1">⚠ {analysis.invalid_nif.length} NIF mal formado(s)</p>
                  <ul className="text-xs text-amber-700 space-y-0.5 max-h-24 overflow-y-auto pl-5 list-disc">
                    {analysis.invalid_nif.slice(0, 10).map((n, i) => (<li key={i}>Fila {n.row} — {n.client_name}: "{n.tax_id}"</li>))}
                  </ul>
                </div>
              )}
              {analysis.rectificativas.length > 0 && (
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
                  <p className="font-bold text-violet-800 text-sm mb-1">↻ {analysis.rectificativas.length} factura(s) rectificativa(s) — irán a la serie R automáticamente</p>
                </div>
              )}

              {/* Suggested counters (mode CONTINUE) */}
              {analysis.suggested_counters.length > 0 && (
                <div className="bg-stone-50 rounded-xl p-3 border border-stone-100">
                  <p className="text-xs font-bold text-stone-700 mb-1">📌 Si eliges "Continuar mi numeración":</p>
                  <ul className="text-xs text-stone-600 space-y-0.5 pl-4 list-disc">
                    {analysis.suggested_counters.map((c, i) => (
                      <li key={i}>Tras importar, la próxima factura en <strong>{c.series}/{c.year}</strong> será la <strong>#{c.next_number}</strong> (max importado: {c.max_imported_number}).</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Mode choice */}
              {analysis.can_import && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-stone-100">
                  <button
                    onClick={() => commitInvoicesAdvanced('continue')}
                    disabled={busy}
                    className="text-left p-4 bg-gradient-to-br from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 border border-orange-200 rounded-xl transition-all disabled:opacity-50"
                    data-testid="commit-continue-mode"
                  >
                    <p className="font-bold text-stone-900 text-sm">A · Continuar mi numeración</p>
                    <p className="text-xs text-stone-600 mt-1">Importa las facturas con su número original y deja la app preparada para emitir la siguiente desde donde quedaste.</p>
                  </button>
                  <button
                    onClick={() => commitInvoicesAdvanced('archive', 'HIST')}
                    disabled={busy}
                    className="text-left p-4 bg-gradient-to-br from-stone-50 to-blue-50 hover:from-stone-100 hover:to-blue-100 border border-stone-200 rounded-xl transition-all disabled:opacity-50"
                    data-testid="commit-archive-mode"
                  >
                    <p className="font-bold text-stone-900 text-sm">B · Empezar serie nueva</p>
                    <p className="text-xs text-stone-600 mt-1">Archiva todo como histórico (serie HIST) sin tocar tus contadores de facturas activas. Útil si vas a usar una serie totalmente nueva.</p>
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={backToEntities}>Cancelar</Button>
            {entity?.key === 'invoices' ? (
              !analysis ? (
                <Button onClick={runAnalysis} disabled={analyzing} className="bg-[#FF6B4A] text-white" data-testid="analyze-btn">
                  {analyzing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Analizando…</> : <>Analizar antes de importar <ChevronRight className="w-4 h-4 ml-1" /></>}
                </Button>
              ) : (
                <Button onClick={() => setAnalysis(null)} variant="outline" data-testid="reanalyze-btn">
                  Re-analizar
                </Button>
              )
            ) : (
              <Button onClick={commitFull} disabled={busy} className="bg-[#FF6B4A] text-white" data-testid="commit-btn">
                {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Importando…</> : <>Importar {preview.total_rows} filas <ChevronRight className="w-4 h-4 ml-1" /></>}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* STEP 4: Result OR live progress */}
      {step === 4 && jobProgress && !result && (
        <div className="bg-white rounded-2xl border border-stone-100 p-6" data-testid="job-progress-card">
          <div className="w-16 h-16 rounded-full bg-orange-100 text-[#FF6B4A] flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
          <h2 className="text-2xl font-bold text-stone-900 text-center">Importando en segundo plano…</h2>
          <p className="text-sm text-stone-500 mt-1 text-center">Puedes cerrar esta página: el trabajo continuará. Vuelve y verás el progreso.</p>
          <div className="mt-6">
            <div className="flex justify-between text-xs text-stone-600 mb-1">
              <span data-testid="job-progress-text">{jobProgress.processed || 0} / {jobProgress.total || 0} filas</span>
              <span><strong>{jobProgress.progress_pct || 0}%</strong></span>
            </div>
            <div className="w-full bg-stone-100 rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#FF6B4A] to-[#FBBF24] transition-all duration-300"
                style={{ width: `${jobProgress.progress_pct || 0}%` }}
                data-testid="job-progress-bar"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-5 text-sm">
            <Stat label="Importados" value={jobProgress.imported || 0} color="bg-emerald-50 text-emerald-700" />
            <Stat label="Duplicados" value={jobProgress.skipped || 0} color="bg-amber-50 text-amber-700" />
            <Stat label="Errores" value={jobProgress.errors_count || 0} color="bg-rose-50 text-rose-700" />
          </div>
        </div>
      )}
      {step === 4 && result && (
        <div className="bg-white rounded-2xl border border-stone-100 p-6 text-center" data-testid="result-card">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-stone-900">Importación completada</h2>
          <p className="text-sm text-stone-500 mt-1">{result.imported} importados · {result.skipped} duplicados saltados · {result.errors?.length || 0} errores</p>
          <div className="grid grid-cols-3 gap-3 mt-5">
            <Stat label="Importados" value={result.imported} color="bg-emerald-50 text-emerald-700" />
            <Stat label="Duplicados" value={result.skipped} color="bg-amber-50 text-amber-700" />
            <Stat label="Errores" value={result.errors?.length || 0} color="bg-rose-50 text-rose-700" />
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-4 text-left bg-rose-50 rounded-xl p-3">
              <p className="text-xs font-bold text-rose-700 mb-1 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Errores</p>
              <ul className="text-xs text-rose-700 space-y-0.5 max-h-32 overflow-y-auto">
                {result.errors.slice(0, 20).map((e, i) => (
                  <li key={i}>Fila {e.row}: {e.error}</li>
                ))}
                {result.errors.length > 20 && <li>… y {result.errors.length - 20} más</li>}
              </ul>
            </div>
          )}
          <div className="flex justify-center gap-2 mt-5">
            <Button onClick={backToEntities} variant="outline">Otra importación</Button>
            <Link to={`/${entity.key}`}><Button className="bg-[#FF6B4A] text-white">Ver {entity.label.toLowerCase()}</Button></Link>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, color }) => (
  <div className={`rounded-xl p-4 ${color}`}>
    <p className="text-3xl font-bold">{value}</p>
    <p className="text-[10px] uppercase font-bold mt-1">{label}</p>
  </div>
);

export default ImportPage;
